import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ScrollText, Library, ExternalLink } from 'lucide-react';

import { useTranslation, Trans } from 'react-i18next';

interface SkillsShellProps {
  activeTab: 'my-skills' | 'library';
  mySkillsCount?: number;
  libraryCount?: number;
  actions?: ReactNode;
  children?: ReactNode;
}

export function SkillsShell({
  activeTab,
  mySkillsCount,
  libraryCount,
  actions,
  children,
}: SkillsShellProps) {
  const { t } = useTranslation('pleri/skills');
  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="h-0.5 bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500" />
      <div className="px-6 pt-4 pb-0">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 shadow-sm">
              <ScrollText className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 leading-tight">
                {t('skills')}
              </h1>
              <p className="mt-0.5 flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                <Trans
                  t={t}
                  i18nKey="customInstructionSetsThatGivePleriSpecialized"
                  components={{
                    a: (
                      <a
                        href="https://docs.pleri.ai/guides/skills/create-your-own-skills"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-600 transition-colors hover:bg-orange-100 dark:border-orange-800/50 dark:bg-orange-950/30 dark:text-orange-400 dark:hover:bg-orange-950/50"
                      />
                    ),
                    externalLink: (
                      <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                    ),
                  }}
                />
              </p>
            </div>
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>

        {/* Primary tabs */}
        <div className="mt-4 flex items-end gap-0">
          <Link
            to="/skills"
            className={`relative flex-shrink-0 px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'my-skills'
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
            }`}
          >
            {t('mySkills')}
            {mySkillsCount !== undefined && mySkillsCount > 0 && (
              <span
                className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums ${
                  activeTab === 'my-skills'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                    : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                }`}
              >
                {mySkillsCount}
              </span>
            )}
            {activeTab === 'my-skills' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
            )}
          </Link>

          <Link
            to="/skills/library"
            className={`relative flex flex-shrink-0 items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'library'
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-gray-400 hover:text-orange-500 dark:text-gray-500 dark:hover:text-orange-400'
            }`}
          >
            <Library className="h-3.5 w-3.5" />
            {t('library')}
            {libraryCount !== undefined && libraryCount > 0 && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums ${
                  activeTab === 'library'
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                    : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                }`}
              >
                {libraryCount}
              </span>
            )}
            {activeTab === 'library' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
            )}
          </Link>
        </div>
      </div>
      {/* Page-specific sub-header (search, scope tabs, tag chips) */}
      {children}
    </div>
  );
}
